"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from "react";
import { DecisionPoint } from "@/helpers/waves/time.types";
import { HorizontalTimelineItem } from "./HorizontalTimelineItem";
interface HorizontalTimelineProps {
  readonly decisions: DecisionPoint[];
  readonly nextDecisionTime: number | null;
  readonly animationComplete?: boolean;
  readonly focus?: "start" | "end" | null;
  readonly onFocusHandled?: () => void;
}

/**
 * Renders a horizontal timeline with decision points
 */
export const HorizontalTimeline: React.FC<HorizontalTimelineProps> = t0 => {
  const $ = _c(17);
  const {
    decisions,
    nextDecisionTime,
    animationComplete: t1,
    focus: t2,
    onFocusHandled
  } = t0;
  const animationComplete = t1 === undefined ? false : t1;
  const focus = t2 === undefined ? null : t2;
  const shouldSpread = decisions.length <= 5;
  const scrollContainerRef = useRef(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const itemRefs = useRef(t3);
  const handledFocusRef = useRef(null);
  let t4;
  let t5;
  if ($[1] !== animationComplete || $[2] !== decisions || $[3] !== focus || $[4] !== nextDecisionTime || $[5] !== onFocusHandled || $[6] !== shouldSpread) {
    t4 = () => {
      if (!scrollContainerRef.current || !animationComplete) {
        return;
      }
      let frameId = null;
      const cleanup = () => {
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
      };
      const markFocusHandled = value => {
        handledFocusRef.current = value;
        onFocusHandled?.();
      };
      const handleFocusScroll = () => {
        if (!focus) {
          return false;
        }
        if (decisions.length === 0) {
          markFocusHandled(focus);
          return true;
        }
        const targetDecision = decisions.at(focus === "start" ? 0 : -1);
        const attemptScroll = () => {
          const targetElement = targetDecision && itemRefs.current[targetDecision.id];
          if (targetDecision && targetElement) {
            markFocusHandled(focus);
            frameId = null;
            targetElement.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: focus === "start" ? "start" : "end"
            });
            return;
          }
          frameId = requestAnimationFrame(attemptScroll);
        };
        attemptScroll();
        return true;
      };
      const resetHandledFocus = () => {
        if (!handledFocusRef.current) {
          return false;
        }
        handledFocusRef.current = null;
        return true;
      };
      const scrollToDecision = (decision, inline) => {
        if (!decision) {
          return false;
        }
        const element = itemRefs.current[decision.id];
        if (!element) {
          return false;
        }
        element.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline
        });
        return true;
      };
      const scrollToNextDecision = () => {
        const index = decisions.findIndex(decision_0 => decision_0.timestamp === nextDecisionTime);
        if (index === -1) {
          return false;
        }
        const anchorIndex = Math.max(index - 3, 0);
        const anchorDecision = decisions[anchorIndex];
        if (scrollToDecision(anchorDecision, anchorIndex === index ? "center" : "start")) {
          return true;
        }
        return scrollToDecision(decisions[index], "center");
      };
      const scrollToLastDecision = () => {
        if (decisions.length === 0 || shouldSpread) {
          return false;
        }
        const lastDecision = decisions[decisions.length - 1];
        return scrollToDecision(lastDecision, "end");
      };
      if (handleFocusScroll() || resetHandledFocus() || scrollToNextDecision() || scrollToLastDecision()) {
        return cleanup;
      }
      return cleanup;
    };
    t5 = [decisions, nextDecisionTime, shouldSpread, animationComplete, focus, onFocusHandled];
    $[1] = animationComplete;
    $[2] = decisions;
    $[3] = focus;
    $[4] = nextDecisionTime;
    $[5] = onFocusHandled;
    $[6] = shouldSpread;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const t6 = `tw-flex tw-gap-4 ${shouldSpread ? "tw-w-full" : "tw-max-w-20"}`;
  let t7;
  if ($[9] !== decisions || $[10] !== nextDecisionTime || $[11] !== shouldSpread) {
    t7 = decisions.map(decision_1 => {
      const isNext = !!nextDecisionTime && decision_1.timestamp === nextDecisionTime;
      return <div key={decision_1.id} ref={el => {
        itemRefs.current[decision_1.id] = el;
      }}><HorizontalTimelineItem decision={decision_1} isNext={isNext} flexGrow={shouldSpread} /></div>;
    });
    $[9] = decisions;
    $[10] = nextDecisionTime;
    $[11] = shouldSpread;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="tw-absolute tw-h-0.5 tw-bg-iron-800 tw-left-0 tw-right-0 tw-top-[20px]" />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t7) {
    t9 = <div className="tw-overflow-hidden tw-rounded-lg"><div className="tw-relative"><div ref={scrollContainerRef} className="tw-overflow-x-auto tw-pb-2 tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300"><div className={t6}>{t7}{t8}</div></div></div></div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
};